import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  InputAdornment,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import LocationCityOutlinedIcon from '@mui/icons-material/LocationCityOutlined';
import MarkunreadMailboxOutlinedIcon from '@mui/icons-material/MarkunreadMailboxOutlined';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AddIcon from '@mui/icons-material/Add';
import { AdresseFacture } from '../models/AdresseFacture';
import { API_BASE_URL } from '../config/api';
import {
  insertAdresseFacturation,
  recupereAdressesFacturation,
  updateAdresseFacturation,
  creerCheckoutSumup,
} from '../Services';
import { useSnackbar } from './SnackbarProvider';

interface LocationState {
  bonId: number;
  urlPaiement: string;
  demandeIds?: number[];
  modePaiementSumup?: boolean;
}

const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 3,
    backgroundColor: '#f8f9fa',
    '&:hover': { backgroundColor: '#f0f1f2' },
    '&.Mui-focused': { backgroundColor: 'white' },
  },
};

const AdresseFacturation: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));
  const { showSnackbar } = useSnackbar();
  const locationState = location.state as LocationState | null;

  const bonId = locationState?.bonId ?? Number(localStorage.getItem('bonId')) ?? 1;
  const urlPaiement =
    locationState?.urlPaiement ??
    localStorage.getItem('urlPaiement') ??
    `${API_BASE_URL}/paiement`;

  const demandeIds = locationState?.demandeIds ?? JSON.parse(localStorage.getItem('demandeIds') || '[]');
  const modePaiementSumup = locationState?.modePaiementSumup ?? localStorage.getItem('modePaiementSumup') === 'true';

  useEffect(() => {
    localStorage.setItem('bonId', bonId.toString());
    localStorage.setItem('urlPaiement', urlPaiement);
    if (demandeIds.length > 0) {
      localStorage.setItem('demandeIds', JSON.stringify(demandeIds));
    }
    localStorage.setItem('modePaiementSumup', modePaiementSumup.toString());
  }, [bonId, urlPaiement, demandeIds, modePaiementSumup]);

  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [adresses, setAdresses] = useState<AdresseFacture[]>([]);
  const [adresse, setAdresse] = useState<AdresseFacture>({
    id: 0,
    id_utilisateur: 0,
    nom: '',
    adresse_ligne1: '',
    adresse_ligne2: '',
    ville: '',
    code_postal: '',
    pays: '',
    telephone: '',
  });
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);

  useEffect(() => {
    const fetchAdressesFacturation = async () => {
      const userJson = localStorage.getItem('user');
      if (userJson) {
        const user = JSON.parse(userJson);
        const adressesFacturation = await recupereAdressesFacturation(user.id_utilisateur);
        if (adressesFacturation && adressesFacturation.length > 0) {
          setAdresses(adressesFacturation);
          const firstAddress = adressesFacturation[0];
          setAdresse({
            id: firstAddress.id || 0,
            id_utilisateur: firstAddress.id_utilisateur || 0,
            nom: firstAddress.nom || '',
            adresse_ligne1: firstAddress.adresse_ligne1 || '',
            adresse_ligne2: firstAddress.adresse_ligne2 || '',
            ville: firstAddress.ville || '',
            code_postal: firstAddress.code_postal || '',
            pays: firstAddress.pays || '',
            telephone: firstAddress.telephone || '',
          });
        }
      }
    };
    fetchAdressesFacturation();
  }, []);

  const handleAdresseChange = (event: any) => {
    const adresseId = event.target.value;
    const idNum = Number(adresseId);
    const nouvelleAdresse = adresses.find((adr) => adr.id === idNum);
    if (nouvelleAdresse) {
      setAdresse({
        id: nouvelleAdresse.id || 0,
        id_utilisateur: nouvelleAdresse.id_utilisateur || 0,
        nom: nouvelleAdresse.nom || '',
        adresse_ligne1: nouvelleAdresse.adresse_ligne1 || '',
        adresse_ligne2: nouvelleAdresse.adresse_ligne2 || '',
        ville: nouvelleAdresse.ville || '',
        code_postal: nouvelleAdresse.code_postal || '',
        pays: nouvelleAdresse.pays || '',
        telephone: nouvelleAdresse.telephone || '',
      });
    }
  };

  const ajouterAdresse = () => {
    setAdresse({
      id: 0,
      id_utilisateur: 0,
      nom: '',
      adresse_ligne1: '',
      adresse_ligne2: '',
      ville: '',
      code_postal: '',
      pays: '',
      telephone: '',
    });
  };

  const validateForm = (): boolean => {
    return (
      !!adresse.nom?.trim() &&
      !!adresse.adresse_ligne1?.trim() &&
      !!adresse.ville?.trim() &&
      !!adresse.code_postal?.trim() &&
      !!adresse.pays?.trim() &&
      !!adresse.telephone?.trim()
    );
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      showSnackbar('Veuillez remplir tous les champs obligatoires.', 'error');
      return;
    }

    try {
      const userJson = localStorage.getItem('user');
      if (userJson) {
        const user = JSON.parse(userJson);
        const idUtilisateur = user.id_utilisateur;

        if (modePaiementSumup && demandeIds.length > 0) {
          setIsProcessingPayment(true);
          const result = await creerCheckoutSumup(demandeIds, idUtilisateur, {
            nom: adresse.nom,
            adresse_ligne1: adresse.adresse_ligne1,
            adresse_ligne2: adresse.adresse_ligne2,
            ville: adresse.ville,
            code_postal: adresse.code_postal,
            pays: adresse.pays,
            telephone: adresse.telephone,
          });
          localStorage.removeItem('demandeIds');
          localStorage.removeItem('modePaiementSumup');
          window.location.href = result.urlPaiement;
          return;
        }

        if (adresse.id === 0) {
          await insertAdresseFacturation(idUtilisateur, bonId, adresse);
        } else {
          await updateAdresseFacturation(idUtilisateur, bonId, adresse);
        }
        setIsFormSubmitted(true);
      }
    } catch (error) {
      console.error("Erreur lors de la soumission de l'adresse :", error);
      setIsProcessingPayment(false);
      showSnackbar('Erreur lors du traitement. Veuillez réessayer.', 'error');
    }
  };

  return (
    <Box
      sx={{
        backgroundImage: 'url(https://beta.visamonde.com/assets/mobile/fond.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: isDesktop ? 640 : 480,
          borderRadius: 4,
          overflow: 'hidden',
          boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
          backgroundColor: 'white',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)',
            p: { xs: 2.5, sm: 3 },
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <IconButton
            onClick={() => navigate('/retour', { state: { tabValue: 2 } })}
            sx={{
              color: 'white',
              bgcolor: 'rgba(255,255,255,0.2)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
              width: 40,
              height: 40,
            }}
          >
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Box>
            <Typography
              variant="h6"
              sx={{ color: 'white', fontWeight: 700, fontSize: { xs: '1.1rem', sm: '1.25rem' } }}
            >
              Adresse de Facturation
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.8rem' }}>
              {modePaiementSumup ? 'Renseignez votre adresse pour procéder au paiement' : 'Renseignez votre adresse de facturation'}
            </Typography>
          </Box>
        </Box>

        {/* Content */}
        <Box sx={{ p: { xs: 2.5, sm: 3.5 } }}>
          {isFormSubmitted ? (
            /* Success state */
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <CheckCircleOutlineIcon sx={{ fontSize: 64, color: '#2e7d32', mb: 2 }} />
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#333', mb: 2 }}>
                Merci ! Votre demande est bien enregistrée.
              </Typography>
              <Box
                sx={{
                  textAlign: 'left',
                  p: 2.5,
                  backgroundColor: '#f8f9fa',
                  borderRadius: 3,
                  mb: 3,
                }}
              >
                <Typography variant="body2" sx={{ color: '#555', lineHeight: 1.7 }} component="div">
                  Pour les dossiers <strong>eVisas</strong>, nous vérifions votre demande en ligne (sous 48 h maximum en service premium ou sous 2 h en service VIP). Recevez ensuite votre lien de paiement directement par mail.
                  <br /><br />
                  Pour les dossiers de <strong>visa sur passeport</strong>, recevez votre lien de paiement par mail dès réception et vérification de vos documents dans nos locaux.
                  <br /><br />
                  Un mail d'informations vous parviendra à chaque étape de votre dossier. Si vous ne recevez rien, merci de bien vouloir vérifier dans vos spams ou courriers indésirables.
                </Typography>
              </Box>
              <Button
                variant="contained"
                fullWidth
                onClick={() => navigate('/retour', { state: { tabValue: 2 } })}
                sx={{
                  py: 1.5,
                  borderRadius: 3,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '1rem',
                  background: 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)',
                  boxShadow: '0 4px 14px rgba(46,125,50,0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #1b5e20 0%, #145218 100%)',
                  },
                }}
              >
                J'ai compris
              </Button>
            </Box>
          ) : (
            /* Form */
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Address selector */}
              {adresses.length > 0 && (
                <FormControl fullWidth sx={inputSx}>
                  <InputLabel id="adresse-select-label">Choisir une adresse</InputLabel>
                  <Select
                    labelId="adresse-select-label"
                    value={adresse.id !== 0 ? adresse.id.toString() : ''}
                    label="Choisir une adresse"
                    onChange={handleAdresseChange}
                    sx={{ borderRadius: 3 }}
                  >
                    {adresses.map((adr) => (
                      <MenuItem key={adr.id} value={adr.id.toString()}>
                        {adr.nom}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              <TextField
                fullWidth
                label="Nom ou raison sociale"
                variant="outlined"
                value={adresse.nom || ''}
                onChange={(e) => setAdresse({ ...adresse, nom: e.target.value })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonOutlineIcon sx={{ color: '#999', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
                sx={inputSx}
              />

              <TextField
                fullWidth
                label="Adresse Ligne 1"
                variant="outlined"
                value={adresse.adresse_ligne1 || ''}
                onChange={(e) => setAdresse({ ...adresse, adresse_ligne1: e.target.value })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <HomeOutlinedIcon sx={{ color: '#999', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
                sx={inputSx}
              />

              <TextField
                fullWidth
                label="Adresse Ligne 2 (optionnel)"
                variant="outlined"
                value={adresse.adresse_ligne2 || ''}
                onChange={(e) => setAdresse({ ...adresse, adresse_ligne2: e.target.value })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <HomeOutlinedIcon sx={{ color: '#999', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
                sx={inputSx}
              />

              {/* Ville + Code postal on same row on desktop */}
              <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                <TextField
                  fullWidth
                  label="Ville"
                  variant="outlined"
                  value={adresse.ville || ''}
                  onChange={(e) => setAdresse({ ...adresse, ville: e.target.value })}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocationCityOutlinedIcon sx={{ color: '#999', fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={inputSx}
                />
                <TextField
                  fullWidth
                  label="Code Postal"
                  variant="outlined"
                  value={adresse.code_postal || ''}
                  onChange={(e) => setAdresse({ ...adresse, code_postal: e.target.value })}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <MarkunreadMailboxOutlinedIcon sx={{ color: '#999', fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={inputSx}
                />
              </Box>

              {/* Pays + Telephone on same row on desktop */}
              <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                <TextField
                  fullWidth
                  label="Pays"
                  variant="outlined"
                  value={adresse.pays || ''}
                  onChange={(e) => setAdresse({ ...adresse, pays: e.target.value })}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PublicOutlinedIcon sx={{ color: '#999', fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={inputSx}
                />
                <TextField
                  fullWidth
                  label="Téléphone"
                  variant="outlined"
                  value={adresse.telephone || ''}
                  onChange={(e) => setAdresse({ ...adresse, telephone: e.target.value })}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneOutlinedIcon sx={{ color: '#999', fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={inputSx}
                />
              </Box>

              {/* Add new address */}
              {adresses.length > 0 && (
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<AddIcon />}
                  onClick={ajouterAdresse}
                  sx={{
                    borderRadius: 3,
                    textTransform: 'none',
                    fontWeight: 600,
                    borderColor: '#2e7d32',
                    color: '#2e7d32',
                    '&:hover': {
                      borderColor: '#1b5e20',
                      backgroundColor: 'rgba(46,125,50,0.04)',
                    },
                  }}
                >
                  Créer une nouvelle adresse
                </Button>
              )}

              {/* Submit */}
              <Button
                variant="contained"
                fullWidth
                onClick={handleSubmit}
                disabled={isProcessingPayment}
                startIcon={isProcessingPayment ? <CircularProgress size={20} color="inherit" /> : undefined}
                sx={{
                  mt: 1,
                  py: 1.5,
                  borderRadius: 3,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '1rem',
                  background: isProcessingPayment
                    ? undefined
                    : 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)',
                  boxShadow: isProcessingPayment
                    ? undefined
                    : '0 4px 14px rgba(46,125,50,0.3)',
                  '&:hover': {
                    background: isProcessingPayment
                      ? undefined
                      : 'linear-gradient(135deg, #1b5e20 0%, #145218 100%)',
                    boxShadow: isProcessingPayment
                      ? undefined
                      : '0 6px 20px rgba(46,125,50,0.4)',
                  },
                }}
              >
                {isProcessingPayment
                  ? 'Redirection vers le paiement...'
                  : modePaiementSumup
                    ? 'Valider et payer'
                    : 'Valider'}
              </Button>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default AdresseFacturation;
